/**
 * WORK-ORDER-FORM-FIELDS.JSX - Form Fields Component for Work Order Creation
 * 
 * SUMMARY:
 * Presentational component that renders all form input fields for work order creation/editing.
 * Includes project selection, quote number, vendor selection, address fields, and file upload.
 * 
 * PROPS:
 * @param {Object} fields - Form field configuration and values
 * @param {boolean} projectId - Whether in project context (hides project selector)
 * @param {Function} projectChangeHandler - Handler for project selection
 * @param {Function} inputChangeHandler - Handler for input changes
 * @param {Function} vendorChangeHandler - Handler for vendor selection
 * @param {Function} changeHandler - Generic field value change handler
 * @param {Function} handleFileUpload - Handler for file uploads
 * @param {Function} handleRemoveFile - Handler for removing new files
 * @param {Function} handleRemoveExistingFile - Handler for removing existing files
 * @param {Function} editVendor - Handler for vendor edit navigation
 * @param {Array} fileToUpload - Array of newly uploaded files
 * @param {Object} poDataRef - Reference to existing PO data
 */

import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectInput from "../../../../components/inputs/select";
import CustomInput from "../../../../components/inputs/new-input";
import FileUploader from "../../../../components/file-uploader";

/**
 * Work Order form fields component.
 * 
 * @component
 * @example
 * <WorkOrderFormFields
 *   fields={fields}
 *   projectId={projectId}
 *   inputChangeHandler={handleInput}
 *   vendorChangeHandler={handleVendor}
 *   handleFileUpload={handleUpload}
 * />
 */
const WorkOrderFormFields = ({
  fields,
  projectId,
  projectChangeHandler,
  inputChangeHandler,
  vendorChangeHandler,
  changeHandler,
  handleFileUpload,
  handleRemoveFile,
  handleRemoveExistingFile,
  editVendor,
  fileToUpload,
  poDataRef
}) => {
  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <div className="input-block d-flex purchase-id-block">
              <label data-cy="work-order-id">Work Order Id</label>
              <p className="mb-0 ms-3"></p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {!projectId && (
          <div className="col-lg-4 col-md-4 mt-3">
            <SelectInput
              {...fields.project}
              label={'Select Project'}
              onChange={(e) => projectChangeHandler(e.target.value)}
              data-cy="select-project"
            />
          </div>
        )}

        <div className="col-lg-4 col-md-4 mt-3">
          <CustomInput
            placeholder="Enter Quote No"
            {...fields.quote_no}
            onChange={(e) => inputChangeHandler(e)}
            data-cy="input-quote_no"
          />
        </div>

        <div className="col-lg-4 col-md-4 mt-3">
          <div className={`vendor-wrapper d-flex align-items-center`}>
            <div className="vendor-select" style={{ flex: '0 0 80%' }}>
              <SelectInput
                {...fields.vendor}
                label={'Vendor'}
                onChange={(e) => !projectId ? vendorChangeHandler(e.target.value) : inputChangeHandler(e)}
                data-cy="select-vendor"
              />
            </div>
            <div
              className="edit-icon"
              style={{ flex: '0 0 20%', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => fields.vendor.value && editVendor(fields.vendor.value)}
              data-cy="create-wo-edit-icon"
            >
              <EditIcon />
            </div>
          </div>
        </div>

        {!projectId && (
          <>
            <div className="col-lg-4 mt-3">
              <CustomInput
                {...fields.street_address}
                onChange={(e) => changeHandler(fields.street_address.name, e.target.value)}
              />
            </div>
            <div className="col-4 mt-3">
              <CustomInput
                {...fields.suburb}
                onChange={(e) => changeHandler(fields.suburb.name, e.target.value)}
              />
            </div>
            <div className="col-4 mt-3">
              <CustomInput
                {...fields.state}
                onChange={(e) => changeHandler(fields.state.name, e.target.value)}
              />
            </div>
            <div className="col-4 mt-3">
              <CustomInput
                {...fields.postcode}
                onChange={(e) => changeHandler(fields.postcode.name, e.target.value)}
              />
            </div>
            <div className="col-4 mt-3">
              <CustomInput
                {...fields.country}
                onChange={(e) => changeHandler(fields.country.name, e.target.value)}
              />
            </div>
          </>
        )}

        <div className="col-lg-4 col-md-4 mt-3 fileupload-section">
          <div className="file-section-part position-relative h-auto px-0">
            <div className="file-part" data-cy="upload-file">
              <FileUploader
                fileHandler={(files) =>
                  handleFileUpload({ target: { files, type: 'file', value: '' } })
                }
              />
              <label htmlFor="#file-section"></label>
            </div>
            <input type="file" id="file-section"></input>
          </div>
        </div>

        <div className="col-12">
          <div className="form-group text-left">
            <div className="input-block">
              <label className="input-label">Uploaded Documents</label>
              <p className="m-0 doc-info">
                {/* Previously uploaded attachments */}
                {Array.isArray(poDataRef.current?.attachment) && poDataRef.current.attachment.map((file, index) => (
                  <span key={`existing-${index}`} className="d-block mb-1">
                    <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
                    <span onClick={() => handleRemoveExistingFile(file.key)}><DeleteIcon /></span>
                  </span>
                ))}

                {/* Newly uploaded files */}
                {Array.isArray(fileToUpload) && fileToUpload.map((file, index) => (
                  <span key={`new-${index}`} className="d-block mb-1">
                    <span>{file.name}</span>
                    <span onClick={() => handleRemoveFile(index)}><DeleteIcon /></span>
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkOrderFormFields;
